import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, apiError, imgUrl } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, LayoutDashboard, Trash2, Sparkles } from "lucide-react";

export default function Moodboards() {
  const navigate = useNavigate();
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", theme: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/moodboards").then((r) => setBoards(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Name your board"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/moodboards", { name: form.name.trim(), theme: form.theme.trim() });
      navigate(`/moodboards/${data.id}`);
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  const remove = async (id, e) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm("Delete this moodboard?")) return;
    try { await api.delete(`/moodboards/${id}`); setBoards(boards.filter((b) => b.id !== id)); toast.success("Deleted"); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">Ideation</p>
          <h1 className="font-heading text-4xl font-bold mt-1 tracking-tight" data-testid="moodboards-title">Moodboards</h1>
          <p className="text-zinc-500 mt-2 text-sm max-w-2xl">Build visual style stories — mix your designs, material swatches, AI imagery, stock photos and color palettes on a free canvas.</p>
        </div>
        <button data-testid="moodboard-create-btn" onClick={() => { setForm({ name: "", theme: "" }); setShowCreate(true); }}
          className="inline-flex items-center gap-2 bg-zinc-950 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800">
          <Plus size={16} /> New moodboard
        </button>
      </div>

      {loading ? (
        <div className="mt-16 flex justify-center"><span className="w-6 h-6 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" /></div>
      ) : boards.length === 0 ? (
        <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400" data-testid="moodboards-empty">
          <LayoutDashboard size={28} className="mx-auto mb-3" />
          <p className="text-sm">No moodboards yet. Create one to start telling your style story.</p>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {boards.map((b) => (
            <Link key={b.id} to={`/moodboards/${b.id}`} data-testid={`moodboard-card-${b.id}`}
              className="group relative border border-zinc-200 bg-white rounded-lg overflow-hidden hover:shadow-md hover:-translate-y-1 transition-all duration-200">
              <div className="grid grid-cols-2 gap-px bg-zinc-100 h-44" style={{ background: b.background }}>
                {b.covers.length === 0 && <div className="col-span-2 flex items-center justify-center text-zinc-300"><Sparkles size={22} /></div>}
                {b.covers.slice(0, 4).map((c, i) => <img key={i} src={imgUrl(c)} alt="" className="w-full h-full object-cover" />)}
              </div>
              <div className="p-4">
                <p className="font-heading font-semibold truncate">{b.name}</p>
                <p className="text-xs text-zinc-400 mt-0.5 truncate">{b.theme || "No theme"} · {b.item_count} item{b.item_count !== 1 ? "s" : ""}</p>
              </div>
              <button data-testid={`moodboard-delete-${b.id}`} onClick={(e) => remove(b.id, e)}
                className="absolute top-2 right-2 bg-white/80 hover:bg-red-50 text-zinc-400 hover:text-red-600 rounded-full p-1.5 border border-zinc-200 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
            </Link>
          ))}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-zinc-950/60 flex items-center justify-center p-4" data-testid="moodboard-create-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold">New moodboard</h2>
              <button onClick={() => setShowCreate(false)} className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3">
              <input data-testid="moodboard-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Board name — e.g. SS26 Coastal"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input data-testid="moodboard-theme-input" value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })} placeholder="Theme / direction (optional) — e.g. romantic 90s revival"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <button data-testid="moodboard-create-confirm" onClick={create} disabled={busy}
                className="w-full bg-zinc-950 text-white py-2.5 rounded-md font-medium hover:bg-zinc-800 disabled:opacity-40">{busy ? "Creating…" : "Create & open"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
